import { useEffect, useRef } from 'react'
import { useUserState } from '~/atoms/UserState'
import WebSocketService from '~/services/WebSocketService'
import {
  WebSocketCommand,
  WebSocketCommandType,
  WebSocketEvent,
  WebSocketEventType,
} from '~/types/WebSocketTypes'

export interface WebSocketSubscription<T extends WebSocketEventType> {
  subscriptionToken: string
  filter?: (event: WebSocketEvent<T>) => boolean
  callback: (event: WebSocketEvent<T>) => void
}

const globalSubscriptions = new Map<
  string,
  WebSocketSubscription<WebSocketEventType>
>()

export const useWebSocketService = () => {
  const { pbxId } = useUserState()
  const socketService = WebSocketService.getInstance(pbxId ?? 0)
  const localSubscriberTokens = useRef<Set<string>>(new Set())

  const getFullToken = (eventType: WebSocketEventType, token: string) => {
    return `${eventType}:${token}`
  }

  const getEventTypeFromToken = (token: string) => {
    return token.split(':')[0] as WebSocketEventType
  }

  const broadcast = (event: WebSocketEvent<WebSocketEventType>) => {
    const eventTypeSubscribers = Array.from(
      localSubscriberTokens.current,
    ).filter((token) => {
      return getEventTypeFromToken(token) === event.eventType
    })

    for (const token of eventTypeSubscribers) {
      const subscription = globalSubscriptions.get(token)
      if (!subscription) {
        continue
      }

      //did the filter work?
      if (subscription.filter && !subscription.filter(event)) {
        continue
      }

      //invoke the callback - filter returned true or there was no filter
      subscription.callback(event)
    }
  }

  const subscribe = <T extends WebSocketEventType>(
    eventType: T,
    subscription: WebSocketSubscription<T>,
  ) => {
    const { subscriptionToken } = subscription
    const fullToken = getFullToken(eventType, subscriptionToken)

    if (!globalSubscriptions.has(fullToken)) {
      //if global subscriptions does not already have this token, add it to the local set
      localSubscriberTokens.current.add(fullToken)
    }

    //if it exists already, replace it
    globalSubscriptions.set(
      fullToken,
      subscription as WebSocketSubscription<WebSocketEventType>,
    )
  }

  const sendCommand = (command: WebSocketCommand<WebSocketCommandType>) => {
    socketService.sendMessage({ ...command })
  }

  useEffect(() => {
    const unsubscribe = socketService.subscribe(broadcast)
    return () => {
      unsubscribe()

      //remove all local subscriptions
      localSubscriberTokens.current.forEach((token) => {
        globalSubscriptions.delete(token)
      })

      //clear the local subscriber tokens
      localSubscriberTokens.current.clear()
    }
  }, [])

  return {
    subscribe,
    sendCommand,
  }
}
